/**
 * Météo via Open-Meteo : API publique, sans clé et sans inscription, ce qui évite
 * de stocker un secret dans une app installable côté client.
 */

export interface DailyForecast {
  date: string;
  code: number;
  label: string;
  emoji: string;
  minC: number;
  maxC: number;
  precipitationMm: number;
  precipitationProbability: number | null;
  windKph: number;
}

/** Codes WMO regroupés en libellés lisibles. */
const WMO: Record<number, { label: string; emoji: string }> = {
  0: { label: 'Ciel dégagé', emoji: '☀️' },
  1: { label: 'Plutôt dégagé', emoji: '🌤️' },
  2: { label: 'Partiellement nuageux', emoji: '⛅' },
  3: { label: 'Couvert', emoji: '☁️' },
  45: { label: 'Brouillard', emoji: '🌫️' },
  48: { label: 'Brouillard givrant', emoji: '🌫️' },
  51: { label: 'Bruine légère', emoji: '🌦️' },
  53: { label: 'Bruine', emoji: '🌦️' },
  55: { label: 'Bruine dense', emoji: '🌦️' },
  56: { label: 'Bruine verglaçante', emoji: '🌧️' },
  57: { label: 'Bruine verglaçante dense', emoji: '🌧️' },
  61: { label: 'Pluie faible', emoji: '🌧️' },
  63: { label: 'Pluie', emoji: '🌧️' },
  65: { label: 'Pluie forte', emoji: '🌧️' },
  66: { label: 'Pluie verglaçante', emoji: '🌧️' },
  67: { label: 'Pluie verglaçante forte', emoji: '🌧️' },
  71: { label: 'Neige faible', emoji: '🌨️' },
  73: { label: 'Neige', emoji: '🌨️' },
  75: { label: 'Neige forte', emoji: '❄️' },
  77: { label: 'Grains de neige', emoji: '❄️' },
  80: { label: 'Averses faibles', emoji: '🌦️' },
  81: { label: 'Averses', emoji: '🌦️' },
  82: { label: 'Averses violentes', emoji: '⛈️' },
  85: { label: 'Averses de neige', emoji: '🌨️' },
  86: { label: 'Averses de neige fortes', emoji: '❄️' },
  95: { label: 'Orage', emoji: '⛈️' },
  96: { label: 'Orage avec grêle', emoji: '⛈️' },
  99: { label: 'Orage violent avec grêle', emoji: '⛈️' },
};

export function describeWeatherCode(code: number): { label: string; emoji: string } {
  return WMO[code] ?? { label: 'Météo inconnue', emoji: '🌡️' };
}

/** Traduit un code WMO vers la catégorie météo utilisée par la fiche jeu. */
export function weatherCodeToGameWeather(code: number): 'sun' | 'rain' | 'snow' | 'any' {
  if (code <= 2) return 'sun';
  if (code >= 71 && code <= 77) return 'snow';
  if (code === 85 || code === 86) return 'snow';
  if (code >= 51) return 'rain';
  return 'any';
}

interface OpenMeteoResponse {
  daily?: {
    time?: string[];
    weather_code?: number[];
    temperature_2m_min?: number[];
    temperature_2m_max?: number[];
    precipitation_sum?: number[];
    precipitation_probability_max?: Array<number | null>;
    wind_speed_10m_max?: number[];
  };
}

/**
 * Prévisions journalières. Open-Meteo ne fournit des prévisions fiables que sur
 * ~16 jours ; au-delà, on renvoie une liste vide plutôt qu'une valeur trompeuse.
 */
export async function fetchForecast(
  latitude: number,
  longitude: number,
  days = 7,
  signal?: AbortSignal,
  timeoutMs = 8_000,
): Promise<DailyForecast[]> {
  const params = new URLSearchParams({
    latitude: latitude.toFixed(4),
    longitude: longitude.toFixed(4),
    daily:
      'weather_code,temperature_2m_min,temperature_2m_max,precipitation_sum,precipitation_probability_max,wind_speed_10m_max',
    timezone: 'Europe/Brussels',
    forecast_days: String(Math.min(16, Math.max(1, days))),
  });

  // Un service tiers qui ne répond jamais laisserait le widget en chargement
  // indéfini : on borne l'attente et on traite le dépassement comme une erreur.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener('abort', onExternalAbort);

  let response: Response;
  try {
    response = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`, {
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onExternalAbort);
  }

  if (!response.ok) throw new Error(`Météo indisponible (${response.status})`);
  const payload = (await response.json()) as OpenMeteoResponse;
  const daily = payload.daily;
  if (!daily?.time) return [];

  return daily.time.map((date, i) => {
    const code = daily.weather_code?.[i] ?? 0;
    const described = describeWeatherCode(code);
    return {
      date,
      code,
      label: described.label,
      emoji: described.emoji,
      minC: Math.round(daily.temperature_2m_min?.[i] ?? 0),
      maxC: Math.round(daily.temperature_2m_max?.[i] ?? 0),
      precipitationMm: Math.round((daily.precipitation_sum?.[i] ?? 0) * 10) / 10,
      precipitationProbability: daily.precipitation_probability_max?.[i] ?? null,
      windKph: Math.round(daily.wind_speed_10m_max?.[i] ?? 0),
    };
  });
}

/** Prévision correspondant à une date donnée, si elle est dans la fenêtre couverte. */
export function forecastForDate(forecasts: DailyForecast[], isoDateTime: string): DailyForecast | null {
  const day = isoDateTime.slice(0, 10);
  return forecasts.find((f) => f.date === day) ?? null;
}
